import { Component, Input, HostListener } from '@angular/core';
import * as Highcharts from 'highcharts';
import { catchError, forkJoin, map, Observable } from 'rxjs';
import { reportSankeyOptions } from '../highcharts-options';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
} from 'src/app/people/quick-build.service';
import { PeopleReportService, ReportResponse } from 'src/app/people/report-service.service';
import { ReportVizChartColors } from '../constants';
import { Log } from 'src/app/logger.service';
import { BaseReportComponent, ChartContextMenuItem } from '../base-report.component';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { SearchFilter } from 'ldt-people-api';
import { ToggleOption } from '../../components/option-toggle/option-toggle.component';
import * as dayjs from 'dayjs';
import { PrevNextFlowComparisonConfig } from '../interfaces/report-config.interfaces';
import { AuthService } from 'src/app/auth/service/auth.service';
import { StatsigService } from '@statsig/angular-bindings';
import { WorkforceLayoutService } from 'src/app/workforce/workforce-layout.service';

const MAX_COMPANIES = 25;

interface PrevNextFlowData {
  quickBuild: QuickBuild;
  prevData: ReportResponse;
  nextData: ReportResponse;
}

@Component({
  selector: 'app-prev-next-flow-comparison',
  templateUrl: './prev-next-flow-comparison.component.html',
  styleUrls: ['./prev-next-flow-comparison.component.scss'],
  standalone: false,
})
export class PrevNextFlowComparisonComponent extends BaseReportComponent<PrevNextFlowComparisonConfig> {
  @Input() quickBuilds: QuickBuild[];
  @Input() dateFrom: string;
  @Input() dateTo: string;

  @Input() allowFieldSelection = true;
  @Input() reportId: string;
  @Input() boxShadow: boolean = true;
  protected readonly CHART_DATA_DESC_PREV =
    'Number of employees who joined the company over the provided date range grouped by their previous companies';
  protected readonly CHART_DATA_DESC_NEXT =
    'Number of employees who departed the company over the provided date range grouped by their next companies';
  @Input() showTitleText = true;

  protected readonly CHART_ID = 'arrivals_departures';

  seriesToggleOptions: ToggleOption[] = [
    { value: 'previous', displayText: 'Previous' },
    { value: 'next', displayText: 'Next' },
    { value: 'both', displayText: 'Both' },
  ];

  @Input()
  set selectedSeries(value: 'previous' | 'next' | 'both' | undefined) {
    if (value) {
      this._selectedSeries = value;
    }
  }
  get selectedSeries(): 'previous' | 'next' | 'both' {
    return this._selectedSeries;
  }

  private _selectedSeries: 'previous' | 'next' | 'both' = 'both';
  private resizeTimeout: any;
  private SMALL_SCREEN_WIDTH = 550;

  constructor(
    reportService: PeopleReportService,
    quickBuildService: QuickBuildService,
    streamsDrawerService: StreamsSubscribeDrawerService,
    auth: AuthService,
    statsigService: StatsigService,
    layoutService: WorkforceLayoutService
  ) {
    super(
      reportService,
      quickBuildService,
      streamsDrawerService,
      auth,
      statsigService,
      layoutService
    );

    // Register configuration properties for automatic emission
    this.registerConfigProperty('selectedSeries', () => this._selectedSeries);
  }

  @HostListener('window:resize', [])
  onResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.updateChartTitle();
    }, 400); // 400ms debounce
  }

  protected initializeChart(): void {
    this.chart = Highcharts.chart(
      `highchartsContainer-${this.chartId}`,
      Highcharts.merge(reportSankeyOptions)
    );
    this.updateChartTitle();
    this.initializeContextMenu();
  }

  protected getChartData(): void {
    if (!this.quickBuilds) return;
    // Set this primarily for the chart subtitles to generate in base-report
    this.quickBuild = this.quickBuilds[0];

    this.showLoading();

    // Get the data for each quick build (Company), joining together so we can draw the chart and send to the AI
    this.subscriptions.add(
      forkJoin(
        this.quickBuilds
          .filter((quickBuild) => quickBuild)
          .map((quickBuild) => {
            return this.getDataForQuickbuild(quickBuild);
          })
      ).subscribe({
        next: (data: (PrevNextFlowData | null)[]) => {
          // In the case of this sankey, we need to get all the data for all the companies and then combine them to generate our series
          const series = this.generateChartSeries(
            data.filter((d) => d !== null) as PrevNextFlowData[]
          );

          this.updateChart(series);

          this.chartLoadingComplete();

          Log.d('JobsMovesComparisonComponent getChartData next');
        },
        error: (error) => {
          Log.e('JobsMovesComparisonComponent getChartData error', error);
          this.showChartError(error);
        },
      })
    );
  }

  // Gets the data for a single quickbuild
  getDataForQuickbuild(quickBuild: QuickBuild): Observable<PrevNextFlowData | null> {
    const reportParams = {
      aggregate_type: 'range',
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: ['jobs.metadata.previous_job.company.name'],
      report_type: 'arrivals',
    };
    const reportParams2 = {
      aggregate_type: 'range',
      date_from: this.dateFrom,
      date_to: this.dateTo,
      group_by: ['jobs.metadata.next_job.company.name'],
      report_type: 'departures',
    };
    const sub = forkJoin([
      this.reportService.getReportDataWithReportDetails(
        this.reportId,
        this.CHART_ID,
        reportParams,
        this.reportTarget,
        quickBuild
      ),
      this.reportService.getReportDataWithReportDetails(
        this.reportId,
        this.CHART_ID,
        reportParams2,
        this.reportTarget,
        quickBuild
      ),
    ]).pipe(
      map((data) => {
        // Make a copy of the data so that we leave the cached data in the report service untouched
        const prev_company_data_copy = JSON.parse(JSON.stringify(data[0]));
        const next_company_data_copy = JSON.parse(JSON.stringify(data[1]));

        // We're going to clean up the response data a bit here to make it easier to work with and better for the AI
        prev_company_data_copy.data.arrivals_departures.forEach((dp: any) => {
          delete dp.date;
          delete dp.departures;
          dp.company_name = dp.group_values[0].value;
          delete dp.group_values;
        });
        next_company_data_copy.data.arrivals_departures.forEach((dp: any) => {
          delete dp.date;
          delete dp.arrivals;
          dp.company_name = dp.group_values[0].value;
          delete dp.group_values;
        });
        const resp: PrevNextFlowData = {
          quickBuild: quickBuild,
          prevData: prev_company_data_copy,
          nextData: next_company_data_copy,
        };
        return resp;
      }),
      catchError((error) => {
        Log.e('JobsMovesComparisonComponent addQuickbuildToChart error', error);
        throw error;
      })
    );
    return sub;
  }

  protected generateChartSeries(data: PrevNextFlowData[]): any[] {
    Log.d('PrevNextFlowComparisonComponent generateChartSeries', data);

    const companyNames = this.quickBuilds.map((d) => d.name);

    // For each dataset, we want to remove any reference to the current battle companies,
    // then sort by most common prev/next and limit the number of companies
    const combinedPrevData: [string, string, number][] = data
      .map((d) => {
        return d.prevData.data.arrivals_departures
          .map((ad: any) => [ad.company_name, d.quickBuild.name, ad.arrivals])
          .filter((ad: string) => ad[0] !== 'None' && !companyNames.includes(ad[0]));
      })
      .flat()
      .sort((a, b) => b[2] - a[2])
      .slice(0, MAX_COMPANIES);

    const combinedNextData: [string, string, number][] = data
      .map((d) => {
        return d.nextData.data.arrivals_departures
          .map((ad: any) => [ad.company_name, d.quickBuild.name, ad.departures])
          .filter((ad: string) => ad[0] !== 'None' && !companyNames.includes(ad[0]));
      })
      .flat()
      .sort((a, b) => b[2] - a[2])
      .slice(0, MAX_COMPANIES);

    Log.d(
      'PrevNextFlowComparisonComponent generateChartSeries',
      combinedPrevData,
      combinedNextData
    );
    let series: Highcharts.SeriesSankeyPointOptionsObject[] = [];

    const includePrevious = this._selectedSeries === 'previous' || this._selectedSeries === 'both';
    const includeNext = this._selectedSeries === 'next' || this._selectedSeries === 'both';

    Log.d(
      'PrevNextFlowComparisonComponent generateChartSeries',
      this._selectedSeries,
      includePrevious,
      includeNext
    );

    if (includePrevious) {
      series = series.concat(
        combinedPrevData.map((c: [string, string, number]) => {
          return {
            type: 'sankey',
            from: 'from-' + c[0],
            to: 'target-' + c[1],
            weight: c[2],
            custom: {
              sourceCompanyLocation: 'to',
              quickBuildId: this.quickBuilds.find((qb) => qb.name === c[1])!.id,
            },
          };
        })
      );
    }

    if (includeNext) {
      series = series.concat(
        combinedNextData.map((c: [string, string, number]) => {
          return {
            type: 'sankey',
            from: 'target-' + c[1],
            to: 'to-' + c[0],
            weight: c[2],
            custom: {
              sourceCompanyLocation: 'from',
              quickBuildId: this.quickBuilds.find((qb) => qb.name === c[1])!.id,
            },
          };
        })
      );
    }

    const nodeIds = Array.from(
      new Set([
        ...series.map((d) => d.from).filter((d) => d !== undefined),
        ...series.map((d) => d.to).filter((d) => d !== undefined),
      ])
    );
    const nodes = nodeIds.map((id) => {
      return {
        id: id,
        name: id!.replace('from-', '').replace('to-', '').replace('target-', ''),
        column: id!.includes('target-') ? (includePrevious ? 1 : 0) : undefined,
      };
    });

    Log.d('PrevNextFlowComparisonComponent generateChartSeries series', series);

    let sData: Highcharts.SeriesSankeyOptions[] = [
      {
        colors: ReportVizChartColors,
        type: 'sankey',
        data: series,
        nodes: nodes,
        keys: ['from', 'to', 'weight'],
        name: 'Employee Flow',
      },
    ];

    return sData;
  }

  protected getContextMenuItems(point: Highcharts.Point): ChartContextMenuItem[] {
    Log.d('PrevNextFlowComparisonComponent getContextMenuItems', point);

    const dateFilterArrivals: UIFilter = {
      filter: {
        field: 'jobs.started_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        date_from: this.dateFrom,
        date_to: this.dateTo,
        type: SearchFilter.TypeEnum.Must,
      },
    };
    const dateFilterDepartures: UIFilter = {
      filter: {
        field: 'jobs.ended_at',
        match_type: SearchFilter.MatchTypeEnum.Fuzzy,
        type: SearchFilter.TypeEnum.Must,
        date_from: this.dateFrom,
        date_to: this.dateTo,
      },
    };

    const anyPoint = point as any; // Not all available properties are documented on the Point type

    // We need to do pretty different things if they click on a node (company) or a point (flow) and whether
    // they've selected previous, next, or both

    // Let's deal with nodes first
    if (anyPoint.formatPrefix === 'node') {
      switch (this._selectedSeries) {
        case 'previous':
          if (anyPoint.column === 1) {
            // They clicked on a battle company node, so we need to filter on just the selected company
            return this.getContextMenuItemsForCurrentCompany(
              anyPoint,
              this.quickBuilds.find((qb) => qb.name === anyPoint.name.trim())!,
              dateFilterArrivals,
              dateFilterDepartures
            );
          } else {
            // They clicked on the previous company node, so we need to filter on prev with that value and
            // company ID is any of the quickbuild companies

            return this.getContextMenuItemsForPrevNextCompany(
              'previous',
              anyPoint.name.trim(),
              dateFilterArrivals
            );
          }
        case 'next':
          if (anyPoint.column === 0) {
            // They clicked on a battle company node, so we need to filter on just the selected company
            return this.getContextMenuItemsForCurrentCompany(
              anyPoint,
              this.quickBuilds.find((qb) => qb.name === anyPoint.name.trim())!,
              dateFilterArrivals,
              dateFilterDepartures
            );
          } else {
            // They clicked on the next company node, so we need to filter on next with that value and
            // company ID is any of the quickbuild companies

            return this.getContextMenuItemsForPrevNextCompany(
              'next',
              anyPoint.name.trim(),
              dateFilterDepartures
            );
          }
        case 'both':
          switch (anyPoint.column) {
            case 1:
              // They clicked on a center (battle company) node, so we need to filter on just the selected company
              return this.getContextMenuItemsForCurrentCompany(
                anyPoint,
                this.quickBuilds.find((qb) => qb.name === anyPoint.name.trim())!,
                dateFilterArrivals,
                dateFilterDepartures
              );
            case 0:
              // They clicked on the previous company node, so we need to filter on prev with that value and
              // company ID is any of the quickbuild companies
              return this.getContextMenuItemsForPrevNextCompany(
                'previous',
                anyPoint.name.trim(),
                dateFilterArrivals
              );

            case 2:
              // They clicked on the next company node, so we need to filter on next with that value and
              // company ID is any of the quickbuild companies

              return this.getContextMenuItemsForPrevNextCompany(
                'next',
                anyPoint.name.trim(),
                dateFilterDepartures
              );
          }
      }
    }

    // Now we know they clicked on a point and we can use custom data on that point to figure out what to do

    const dir = anyPoint.custom?.sourceCompanyLocation;
    const filterField =
      dir === 'from' ? 'jobs.next_job.company.name' : 'jobs.previous_job.company.name';
    const companyOfInterest =
      dir === 'from'
        ? anyPoint.to.replace('to-', '').replace('from-', '')
        : anyPoint.from.replace('to-', '').replace('from-', '');

    const flowFilter: UIFilter = {
      filter: {
        field: filterField,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [companyOfInterest],
      },
    };

    let dateFilter = dateFilterArrivals;
    if (dir === 'from') {
      dateFilter = dateFilterDepartures;
    }

    const qbCopy = QuickBuildService.copyQuickBuild(
      this.quickBuilds.find((qb) => qb.id === anyPoint.custom?.quickBuildId)!
    );

    return [
      {
        label: `View People flow ${dir === 'from' ? 'from' : 'to'} ${qbCopy.name} ${dir === 'from' ? 'to' : 'from'} ${companyOfInterest}`,
        icon: 'swap_horiz',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilter, flowFilter], 'moneyball');
          qbCopy.name = `People flow ${dir === 'from' ? 'from' : 'to'} ${qbCopy.name} ${dir === 'from' ? 'to' : 'from'} ${companyOfInterest} from ${dayjs(this.dateFrom).format('MMM YYYY')} to ${dayjs(this.dateTo).format('MMM YYYY')}`;
          this.openDrawer(qbCopy);
        },
      },
    ];
  }

  private getContextMenuItemsForPrevNextCompany(
    direction: 'previous' | 'next',
    filterValue: string,
    dateFilter: UIFilter
  ): ChartContextMenuItem[] {
    // They clicked on the next company node, so we need to filter on next with that value and
    // company ID is any of the quickbuild companies
    const filterField =
      direction === 'next' ? 'jobs.next_job.company.name' : 'jobs.previous_job.company.name';
    const flowFilter: UIFilter = {
      filter: {
        field: filterField,
        match_type: SearchFilter.MatchTypeEnum.Exact,
        type: SearchFilter.TypeEnum.Must,
        string_values: [filterValue],
      },
    };
    const qb = this.generateQuickbuildForAllCompanies(this.quickBuilds);
    return [
      {
        label: `View People flow ${direction === 'next' ? 'to' : 'from'} ${filterValue}`,
        icon: 'swap_horiz',
        action: (p) => {
          QuickBuildService.addFiltersToFilterGroup(qb, [dateFilter, flowFilter], 'moneyball');
          qb.name = `People flow ${direction === 'next' ? 'to' : 'from'} ${filterValue} ${direction === 'next' ? 'from' : 'to'} any target company from ${dayjs(this.dateFrom).format('MMM YYYY')} to ${dayjs(this.dateTo).format('MMM YYYY')}`;
          this.openDrawer(qb);
        },
      },
    ];
  }

  // Creates a new quickbuild with a filter on all the companies in the battle
  private generateQuickbuildForAllCompanies(quickBuilds: QuickBuild[]): QuickBuild {
    const qb = QuickBuildService.copyQuickBuild(quickBuilds[0]);
    const companyFilter = QuickBuildService.findFilterGroupById('moneyball', qb.rootFilterGroup);
    ((companyFilter!.filters[0] as UIFilter).filter as SearchFilter).string_values =
      quickBuilds.map((qb) => {
        const jobGroup = QuickBuildService.findFilterGroupById('moneyball', qb.rootFilterGroup);
        return ((jobGroup!.filters[0] as UIFilter)!.filter! as SearchFilter).string_values![0];
      });
    return qb;
  }

  // For the current company, we're going to gather all arrivals or departures or both over the date range, depending on the selected series
  private getContextMenuItemsForCurrentCompany(
    point: Highcharts.Point,
    qb: QuickBuild,
    dateFilterArrivals: UIFilter,
    dateFilterDepartures: UIFilter
  ): ChartContextMenuItem[] {
    const qbCopy = QuickBuildService.copyQuickBuild(qb);

    const prevItem = {
      label: `View People flow into ${qbCopy.name}`,
      icon: 'swap_horiz',
      action: (p: any) => {
        QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilterArrivals], 'moneyball');
        const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
        group!.positionStatus = PositionStatus.First;
        qbCopy.name =
          'People flow into ' +
          qbCopy.name +
          ' from ' +
          dayjs(this.dateFrom).format('MMM YYYY') +
          ' to ' +
          dayjs(this.dateTo).format('MMM YYYY');

        this.openDrawer(qbCopy);
      },
    };

    const nextItem = {
      label: `View People flow out of ${qbCopy.name}`,
      icon: 'swap_horiz',
      action: (p: any) => {
        QuickBuildService.addFiltersToFilterGroup(qbCopy, [dateFilterDepartures], 'moneyball');
        const group = QuickBuildService.findFilterGroupById('moneyball', qbCopy.rootFilterGroup);
        group!.positionStatus = PositionStatus.Last;
        qbCopy.name =
          'People flow out of ' +
          qbCopy.name +
          ' from ' +
          dayjs(this.dateFrom).format('MMM YYYY') +
          ' to ' +
          dayjs(this.dateTo).format('MMM YYYY');
        Log.d('PrevNextFlowComparisonComponent getContextMenuItemsForCurrentCompany', qbCopy);
        this.openDrawer(qbCopy);
      },
    };

    if (this._selectedSeries === 'previous') {
      return [prevItem];
    }

    if (this._selectedSeries === 'next') {
      return [nextItem];
    }

    return [prevItem, nextItem];
  }

  onSeriesChanged(value: string) {
    if (!value) return;

    this._selectedSeries = value as 'previous' | 'next' | 'both';
    this.emitConfiguration();
    this.resetChartData();
    this.getChartData();
  }

  private updateChartTitle(): void {
    if (this.chart) {
      this.chart.update(
        {
          title: {
            text: this.showTitleText ? 'Top Talent Flow' : undefined,
            align: window.innerWidth <= this.SMALL_SCREEN_WIDTH ? 'left' : 'center',
          },
        },
        true,
        true
      );
    }
  }

  ngOnDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    super.ngOnDestroy?.();
  }
}
